import { useEffect, useState } from 'react';
import type { SignoutReason } from '../../api/client.js';
import { requestOtp, verifyOtp, type OtpChallenge } from '../../api/endpoints.js';
import { useAction } from '../../api/hooks.js';
import { Brand, BrandMark } from '../app.js';
import { Trouble } from '../ui.js';

/**
 * The console's own door. The credential is the same one every user has — a
 * phone number and a one-time code, because the backend issues no other (spec
 * 2026-08-29 §2) — but this is not the consumer flow: no terms box, no
 * onboarding, and the copy says what unlocks the console. The session it
 * opens lives under the console's own storage key (client.ts, sessionRealm),
 * so it never becomes a consumer session.
 */

const CODE_LENGTH = 6;
const E164_RE = /^\+[1-9]\d{7,14}$/;

/** People type "+91 85558 14854" or "+1 (555) 123-4567". Same number. */
function tidyPhone(raw: string): string {
  return raw.replace(/[\s().-]/g, '');
}

function secondsUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 1000));
}

const SIGNOUT_HEADLINE: Record<string, string> = {
  TOKEN_REUSED: 'You were signed out for safety: this session was used from somewhere else.',
  ACCOUNT_DELETING: 'You were signed out: this account is being deleted.',
  FORBIDDEN: 'You were signed out: this account is no longer active.',
};

export function AdminSignIn({ notice }: { notice: SignoutReason | null }) {
  const [phone, setPhone] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const request = useAction(requestOtp, setChallenge);

  const tidy = tidyPhone(phone);
  const phoneOk = E164_RE.test(tidy);

  if (challenge !== null) {
    return (
      <CodeStage
        phone={tidy}
        challenge={challenge}
        onBack={() => {
          setChallenge(null);
          request.reset();
        }}
        onNewChallenge={setChallenge}
      />
    );
  }

  return (
    <form
      className="cr-login"
      onSubmit={(event) => {
        event.preventDefault();
        if (phoneOk && !request.pending) request.run(tidy);
      }}
    >
      <div className="cr-brand">
        <BrandMark />
        <Brand />
      </div>
      <h1 className="cr-login-title">Sign in</h1>
      <p>Enter your phone number and we will text you a code. Access to the console is granted per account.</p>
      {notice !== null && (
        <div className="cr-notice bad" role="alert">
          <strong>{SIGNOUT_HEADLINE[notice.code] ?? 'You were signed out.'}</strong>
          <span>{notice.message}</span>
        </div>
      )}
      <label className="cr-field">
        <span>
          Phone number <small>with country code, e.g. +1 555 123 4567</small>
        </span>
        <input
          className="big"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+1 555 123 4567"
          value={phone}
          disabled={request.pending}
          onChange={(event) => setPhone(event.target.value)}
        />
      </label>
      <button type="submit" className="cr-btn primary full" disabled={!phoneOk || request.pending}>
        {request.pending ? 'Sending the code…' : 'Send code'}
      </button>
      {request.error !== null && <Trouble error={request.error} />}
      <div className="cr-login-foot">
        <span>Actions in the console are recorded under your name.</span>
        <a href="/">Open the app</a>
      </div>
    </form>
  );
}

function CodeStage({
  phone,
  challenge,
  onBack,
  onNewChallenge,
}: {
  phone: string;
  challenge: OtpChallenge;
  onBack: () => void;
  onNewChallenge: (challenge: OtpChallenge) => void;
}) {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(() => secondsUntil(challenge.resend_after));

  // verifyOtp stores the pair itself; the shell's useSignedIn flips and this
  // screen unmounts. The `onboarding` block in the response is ignored here:
  // an operator is not being onboarded.
  const verify = useAction(verifyOtp);
  const resend = useAction(requestOtp, (fresh) => {
    onNewChallenge(fresh);
    setCode('');
    setCooldown(secondsUntil(fresh.resend_after));
  });

  useEffect(() => {
    const timer = window.setInterval(() => setCooldown(secondsUntil(challenge.resend_after)), 1000);
    return () => window.clearInterval(timer);
  }, [challenge.resend_after]);

  useEffect(() => {
    if (code.length === CODE_LENGTH && !verify.pending) verify.run(challenge.challenge_id, code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="cr-login">
      <div className="cr-brand">
        <BrandMark />
        <Brand />
      </div>
      <h1 className="cr-login-title">Enter the code</h1>
      <p>
        We sent a six-digit code to <strong>{phone}</strong>. It is valid for five minutes and checks
        itself once all six digits are in.
      </p>
      <label className="cr-field">
        <span>Verification code</span>
        <input
          className="big code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          value={code}
          disabled={verify.pending}
          autoFocus
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
            verify.reset();
          }}
        />
      </label>
      {verify.pending && <p>Checking…</p>}
      {verify.error !== null && <Trouble error={verify.error} />}
      <div className="cr-actions">
        <button
          type="button"
          className="cr-link"
          disabled={cooldown > 0 || resend.pending}
          onClick={() => resend.run(phone)}
        >
          {resend.pending ? 'Sending…' : cooldown > 0 ? `Resend code in ${String(cooldown)}s` : 'Resend code'}
        </button>
        <button type="button" className="cr-link" onClick={onBack}>
          Use a different number
        </button>
      </div>
      {resend.error !== null && <Trouble error={resend.error} />}
    </div>
  );
}
